// react
import { SyntheticEvent, useContext, useState } from 'react';

// types
import {
	Act,
	Activity,
	Contract,
	Document,
	Entity,
	Pagination,
	Transaction,
	User,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// ui
import { BoxDescription } from '../../ui/BoxDescription/BoxDescription';
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { Tabs, TabsProps } from '../../ui/Tabs/Tabs';
import { Time } from '../../ui/Time/Time';
import { UserAvatar } from '../../ui/UserAvatar/UserAvatar';

// tables
import { TableBasicContracts } from '../../tables/basic/TableBasicContracts/TableBasicContracts';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';
import { TableBasicEntities } from '../../tables/basic/TableBasicEntities/TableBasicEntities';
import { TableBasicTransactions } from '../../tables/basic/TableBasicTransactions/TableBasicTransactions';

// dialogs
import { DialogAddTransaction } from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogEditAct } from '../../dialogs/DialogEditAct/DialogEditAct';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';

// theme
import { theme } from '../../../theme';

// icons
import {
	InsertDriveFileOutlined as IconInsertDriveFile,
	ModeEditOutlined as IconModeEdit,
	PaidOutlined as IconPaid,
} from '@mui/icons-material';

// utils
import { defaultPagination } from '../../../utils/defaults';

// props
export interface CardActProps {
	act: Act;
	refetchActs?: () => void;
}

export const CardAct = ({ act, refetchActs }: CardActProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogAct = searchParams.get('dialogAct');

	// state
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [tabValue, setTabValue] = useState('description');
	const [transactions, setTransactions] = useState<Transaction[]>([]);

	// fetch suppliers
	const { isLoading: isLoadingEntities, refetch: refetchEntities } = useFetch({
		isEnabled: Boolean(isExpanded && tabValue === 'suppliers'),
		url: `${process.env.REACT_APP_API_URL}/entities?acts=${act.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch contracts
	const { isLoading: isLoadingContracts, refetch: refetchContracts } = useFetch({
		isEnabled: Boolean(isExpanded && tabValue === 'contracts'),
		url: `${process.env.REACT_APP_API_URL}/contracts?acts=${act.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}
		},
	});

	// fetch documents
	const { isLoading: isLoadingDocuments, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(tabValue === 'documents'),
		url: `${process.env.REACT_APP_API_URL}/documents?act=${act.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// fetch transactions
	const { isLoading: isLoadingTransactions } = useFetch({
		isEnabled: Boolean(isExpanded && tabValue === 'transactions'),
		url: `${process.env.REACT_APP_API_URL}/transactions?acts=${act.id}&populate=expenseType`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}
		},
	});

	const tabs: TabsProps['tabs'] = [
		{
			label: t('description'),
			value: 'description',
		},
	];

	if (userProjectPermissions?.supplier?.read) {
		tabs.push({
			label: t('suppliers'),
			value: 'suppliers',
		});
	}

	if (userProjectPermissions?.contract?.read) {
		tabs.push({
			label: t('contracts'),
			value: 'contracts',
		});
	}

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	if (userProjectPermissions?.payment?.read) {
		tabs.push({
			label: t('payments'),
			value: 'transactions',
		});
	}

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogAct');
		setSearchParams(searchParams);
	};

	const btnMenuItems = [];

	if (userProjectPermissions?.payment?.create) {
		if (act.status === 'READY') {
			btnMenuItems.push({
				children: t('recordPayment'),
				icon: <IconPaid />,
				onClick: () => {
					searchParams.set('dialog', 'recordPayment');
					searchParams.set('dialogAct', String(act.id));
					setSearchParams(searchParams);
				},
			});
		}
	}

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			children: t('uploadDocuments'),
			icon: <IconInsertDriveFile />,
			onClick: () => {
				searchParams.set('dialog', 'uploadDocuments');
				searchParams.set('dialogAct', String(act.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.act?.update) {
		btnMenuItems.push({
			children: t('editActivity'),
			icon: <IconModeEdit />,
			onClick: () => {
				searchParams.set('dialog', 'editAct');
				searchParams.set('dialogAct', String(act.id));
				setSearchParams(searchParams);
			},
		});
	}

	let userModified = null;

	if (act.activities.length) {
		userModified = (act.activities as Activity[])[0].actor as User;
	}

	return (
		<>
			<Paper sx={{ position: 'relative' }}>
				<Box
					sx={(theme) => {
						return {
							padding: theme.spacing(2),
						};
					}}>
					<Grid alignItems="center" container spacing={2}>
						<Grid item xs={12} lg={3}>
							<Grid alignItems="center" container spacing={2}>
								<Grid item>
									<IconCircle type="act" />
								</Grid>
								<Grid flex={1} item>
									<Typography variant="h3">{act.name}</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('duration')}
								value={
									<Grid container>
										<Grid item xs={12}>
											<Time date={act.dateStart} /> -
										</Grid>
										<Grid item xs={12}>
											<Time date={act.dateEnd} />
										</Grid>
									</Grid>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label="Created" value={<Time date={act.created} />} />
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<Grid container spacing={1}>
										<Grid item xs={12}>
											<UserAvatar
												GridProps={{
													sx: {
														justifyContent: { xs: 'flex-end', lg: 'flex-start' },
													},
												}}
												user={userModified}
											/>
										</Grid>
										<Grid item xs={12}>
											<Time date={act.modified} />
										</Grid>
									</Grid>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={act.status} />} />
						</Grid>
						<Grid item xs={12} lg={1}>
							<RowKeyValue
								label={t('documents')}
								value={act.documents.length ? <IconCircle size="xs" type="check" /> : t('na')}
							/>
						</Grid>
						<Grid item xs={12} lg={true}>
							<RowKeyValue
								label={t('payments')}
								value={act.transactions.length ? <IconCircle size="xs" type="check" /> : t('na')}
							/>
						</Grid>
						<Grid item xs={12} lg="auto">
							<Grid container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={() => {
											setIsExpanded(!isExpanded);
										}}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<Box>
						<TabContext value={tabValue}>
							<Tabs
								BoxProps={{
									sx: {
										marginTop: {
											lg: theme.spacing(3),
										},
									},
								}}
								hasBorder={false}
								onChange={handleChangeTab}
								tabs={tabs}
								value={tabValue}
							/>
							<TabPanel value="suppliers">
								<TableBasicEntities data={entities} isLoading={isLoadingEntities} />
							</TabPanel>
							<TabPanel value="contracts">
								<TableBasicContracts data={contracts} isLoading={isLoadingContracts} />
							</TabPanel>
							<TabPanel value="documents">
								<TableBasicDocs
									data={documents}
									isLoading={isLoadingDocuments}
									pagination={paginationDocuments}
									refetchDocuments={refetchDocuments}
								/>
							</TabPanel>
							<TabPanel value="transactions">
								<TableBasicTransactions data={transactions} isLoading={isLoadingTransactions} />
							</TabPanel>
							<TabPanel value="description">
								<BoxDescription description={act.description} />
							</TabPanel>
						</TabContext>
					</Box>
				)}
			</Paper>
			<DialogAddTransaction
				DialogActionsProps={{ subtitle: { children: act.name } }}
				isOpen={paramDialog === 'recordPayment' && paramDialogAct === String(act.id)}
				onClose={handleCloseDialog}
				type="PAYMENT"
			/>
			<DialogEditAct
				isOpen={paramDialog === 'editAct' && paramDialogAct === String(act.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchActs) {
						refetchActs();
					}
					refetchContracts();
					refetchEntities();
				}}
			/>
			<DialogUploadDocs
				act={act}
				isOpen={paramDialog === 'uploadDocuments' && paramDialogAct === String(act.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchActs) {
						refetchActs();
					}
					refetchDocuments();
				}}
			/>
		</>
	);
};
